"use client";

const TOKEN_KEY = "ip_avatar_token";
const USER_KEY = "ip_avatar_user";

export type User = {
  id: number;
  username: string;
  wechat_nickname: string;
  is_admin: boolean;
  is_active: boolean;
  created_at?: string;
  last_login_at?: string;
  expires_at?: string | null;
  quota_expired?: boolean;
  quota_unlimited?: boolean;
};

export class ApiError extends Error {
  status: number;
  code?: string;
  payload?: any;
  constructor(message: string, status: number, code?: string, payload?: any) {
    super(message);
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

export function isQuotaExpiredError(e: unknown): e is ApiError {
  return e instanceof ApiError && e.code === "quota_expired";
}

function errorFromResponse(status: number, j: any, fallback: string): ApiError {
  const detail = j?.detail;
  if (detail && typeof detail === "object") {
    return new ApiError(detail.message || fallback, status, detail.code, detail);
  }
  return new ApiError(typeof detail === "string" ? detail : fallback, status);
}

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuth(token: string, user: User) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function saveUser(user: User) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function getUser(): User | null {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(USER_KEY);
  return raw ? (JSON.parse(raw) as User) : null;
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

function authHeaders(): Record<string, string> {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export async function api<T = any>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const res = await fetch(`/api${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    let err = new ApiError(`请求失败 (${res.status})`, res.status);
    try {
      const j = await res.json();
      err = errorFromResponse(res.status, j, err.message);
    } catch {
      /* ignore */
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * multipart 上传。不能走 api()：那里固定带 application/json，会把 FormData 的
 * boundary 冲掉，后端解析不出字段。
 */
export async function apiUpload<T = any>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });
  if (!res.ok) {
    let err = new ApiError(`上传失败 (${res.status})`, res.status);
    try {
      const j = await res.json();
      err = errorFromResponse(res.status, j, err.message);
    } catch {
      /* ignore */
    }
    throw err;
  }
  return (await res.json()) as T;
}

/**
 * SSE 流式发送消息。onEvent 收到 (eventType, dataObject)。
 */
export async function streamChat(
  body: { conversation_id: number | null; message: string },
  onEvent: (event: string, data: any) => void,
  signal?: AbortSignal
): Promise<void> {
  const res = await fetch("/api/chat/send", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) {
    let err = new ApiError(`请求失败 (${res.status})`, res.status);
    try {
      const j = await res.json();
      err = errorFromResponse(res.status, j, err.message);
    } catch {
      /* ignore */
    }
    throw err;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    // sse-starlette 用 \r\n 作行分隔，这里统一归一化成 \n，
    // 否则以 "\n\n" 切分事件块会永远匹配不到（实际是 "\r\n\r\n"）。
    buffer += decoder.decode(value, { stream: true }).replace(/\r/g, "");

    // 以空行分隔的 SSE 事件块
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const rawEvent = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);

      let eventType = "message";
      const dataLines: string[] = [];
      for (const line of rawEvent.split("\n")) {
        if (line.startsWith("event:")) eventType = line.slice(6).trim();
        else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
      }
      if (dataLines.length === 0) continue;
      const dataStr = dataLines.join("\n");
      try {
        onEvent(eventType, JSON.parse(dataStr));
      } catch {
        onEvent(eventType, dataStr);
      }
    }
  }
}
